import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { Icon } from '@wartek-id/icon'
import { Input, InputGroup, InputLeftAddon, InputProps } from '@wartek-id/input'

const DEBOUNCE_DURATION_DEFAULT = 1000

interface PropsInputSearch extends Omit<InputProps, 'onChange'> {
  debounceDuration?: number
  isLoading?: boolean
  onChange?: (value: string) => any
}

export type RefInputSearch = {
  resetValue: () => void
}

const InputSearch = (
  {
    debounceDuration = DEBOUNCE_DURATION_DEFAULT,
    isLoading = false,
    onChange = () => {},
    ...inputProps
  }: PropsInputSearch,
  ref
) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [value, setValue] = useState('')
  const debouncedSetValue = useDebouncedCallback((newValue) => {
    setValue(newValue)
  }, debounceDuration)

  const resetValue = () => {
    inputRef.current.value = ''
    setValue('')
  }

  useImperativeHandle(ref, () => ({
    resetValue,
  }))

  useEffect(() => {
    onChange(value.trim())
    setIsSearching(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <InputGroup>
      <InputLeftAddon>
        {isSearching || (!!value && isLoading) ? (
          <Icon fontSize="sm" className="animate-spin">
            autorenew
          </Icon>
        ) : (
          <Icon fontSize="sm">search</Icon>
        )}
      </InputLeftAddon>
      <Input
        ref={inputRef}
        onChange={(e) => {
          setIsSearching(true)
          const { value: newValue } = e.target

          if (!newValue) {
            setValue('')
          }
          debouncedSetValue(newValue)
        }}
        onKeyDown={(e) => {
          if (e.code === 'Enter') {
            setValue((e.target as EventTarget & HTMLInputElement).value)
          }
        }}
        size="sm"
        placeholder="Cari"
        className="!border-subdued !pr-10 !text-sm"
        clearable
        {...inputProps}
      />
    </InputGroup>
  )
}

export default forwardRef<RefInputSearch, PropsInputSearch>(InputSearch)
